import React from 'react';
import I18n from 'i18n-js';
import { RCGuidePopover } from '@reverbdotcom/cadence/components';
import { useUser } from '../user_hooks';
import { setLoggedOutUserActions, getLoggedOutUserActions, loggedOutUserActionNames } from './logged_out_user_actions';
import { MParticleEventName, trackEvent } from '../elog/mparticle_tracker';
import { formatExperimentsForEventAttributes } from '../elog/mparticle';
import experiments from '../experiments';
import { useCreateUserAction } from '../user_actions/create_action_mutation';
import { isExperimentEnabled } from '../user_context_helpers';
import { CoreUserActionsCreateMutationFn } from '../user_actions/create_action_mutation';
import { availableOfferButtonAction } from './create_offer_button';
import {
  core_apimessages_UserActionName,
  Item2AvailableActionsData,
  CreateUserActionMeData,
  ListingCreateOfferButtonData,
} from '../rql';

interface IProps {
  anchor: JSX.Element;
  availableActions?: Item2AvailableActionsData.Fragment[];
  createUserAction?: CoreUserActionsCreateMutationFn;
  listing: ListingCreateOfferButtonData.Fragment;
  currentView?: string;
  me?: CreateUserActionMeData.Fragment;
}

export function SellerOpenToOffersPopover({
  anchor,
  listing,
  availableActions,
  createUserAction,
  currentView,
  me,
}: IProps) {
  const user = useUser();
  const isListingPage = currentView === 'listing';
  const loggedInUserActionAvailable = me && availableOfferButtonAction(availableActions);

  const loggedOutExpEnabled = isExperimentEnabled(user, experiments.OPEN_TO_OFFERS_LOGGED_OUT);
  const loggedOutUserActions = getLoggedOutUserActions();
  const loggedOutUserActionAvailable = user?.loggedOut &&
    !loggedOutUserActions.find(action => action.name === loggedOutUserActionNames.VIEWED_SELLER_OPEN_TO_OFFERS_CALLOUT);

  const createUserActionAvailable = loggedInUserActionAvailable || (loggedOutUserActionAvailable && loggedOutExpEnabled);
  const createUserActionState = useCreateUserAction(
    core_apimessages_UserActionName.SELLER_OPEN_TO_OFFERS_CALLOUT,
    me,
    {
      listingId: listing.id,
    },
    createUserAction,
  );

  const [isOpen, setIsOpen] = React.useState(!!createUserActionAvailable && isListingPage);

  React.useEffect(() => {
    if (loggedOutUserActionAvailable && isListingPage) {
      setLoggedOutUserActions({
        name: loggedOutUserActionNames.VIEWED_SELLER_OPEN_TO_OFFERS_CALLOUT,
        created: Date.now(),
      });
      trackEvent({
        eventName: MParticleEventName.ViewedOfferCallout,
        listingId: listing.id,
        listing: listing,
        componentName: 'SellerOpenToOffersPopover',
        experiments: formatExperimentsForEventAttributes(user, [experiments.OPEN_TO_OFFERS_LOGGED_OUT]),
        role: 'buyer',
      });
    }
  }, []);

  function onDismiss() {
    createUserActionState.createUserAction();
    setIsOpen(false);
  }

  return (
    <RCGuidePopover
      title={I18n.t('commons.offers.sellerOpenToOffersPopover.title')}
      content={I18n.t('commons.offers.sellerOpenToOffersPopover.content')}
      isOpen={isOpen}
      onDismiss={onDismiss}
      anchor={anchor}
      preventAutoFocus
    />
  );
}
